import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { useAddAccountsToGroupMutation } from 'api/groups/account/addAccountsToGroup'
import { useLazyGetAccountGroupListQuery } from 'api/groups/account/getAccountGroupList'
import CancelButton from 'components/buttons/CancelButton'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHFormSelectField from 'components/form-fields/v5/HHFormSelectField'
import T from 'T'
import { handleError } from 'utils/error'

import AddEditAccountGroupDialog from './AddEditAccountGroupDialog'

import type { AccountGroupListItemApi } from 'types/api/account-groups/accountGroup'

type Props = {
  isOpen?: boolean
  accountIds?: string[]
  onAddToGroup?: (accountGroupId: string) => void
  onClose: () => void
}

type AddToGroupValues = { accountGroupId: string }

const DEFAULT_VALUES: AddToGroupValues = { accountGroupId: '' }
const REQUIRED_RULES = { required: T.CANNOT_BE_EMPTY }
const SPACER_SX = { flex: '1 1 auto' }
const NO_ACCOUNTS: string[] = []

const createdAtTime = ({ createdAt }: AccountGroupListItemApi) => (createdAt ? new Date(createdAt).getTime() || 0 : 0)

const AddToGroupDialog: React.FC<Props> = ({ isOpen = false, onAddToGroup, accountIds = NO_ACCOUNTS, onClose }) => {
  const [isOpenAddEditAccountGroupDialog, setIsOpenAddEditAccountGroupDialog] = useState(false)
  const [getAccountGroupList, { data: accountGroupList }] = useLazyGetAccountGroupListQuery()
  const [addAccountsToGroup, { isLoading: isAddToGroupLoading }] = useAddAccountsToGroupMutation()
  const addToGroupForm = useForm<AddToGroupValues>({ defaultValues: DEFAULT_VALUES })
  const {
    control,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = addToGroupForm
  const accountGroupIdHelperText = errors.accountGroupId?.message

  const groupOptions = useMemo(() => {
    const groups: AccountGroupListItemApi[] = accountGroupList?.accountGroups ?? []
    return [...groups]
      .sort((a, b) => createdAtTime(b) - createdAtTime(a))
      .map(({ groupId, groupName }) => ({ key: groupId, value: groupId, label: groupName }))
  }, [accountGroupList])

  const handleCreateNewGroup = useCallback(() => setIsOpenAddEditAccountGroupDialog(true), [])
  const handleCreateNewGroupSuccess = useCallback((id: string) => setValue('accountGroupId', id), [setValue])
  const handleCloseCreateNewGroup = useCallback(() => setIsOpenAddEditAccountGroupDialog(false), [])

  const handleAdd = useMemo(
    () =>
      handleSubmit(({ accountGroupId }) => {
        // AddAgingAccountsToAccountGroup adds its own accounts and passes none here.
        onAddToGroup?.(accountGroupId)
        if (accountIds.length === 0) return
        addAccountsToGroup({ accountGroupId, accounts: accountIds })
          .unwrap()
          .then(() => {
            toast.success(T.ADD_ACCOUNT_TO_GROUP_SUCCESS_MSG)
            onClose()
          })
          .catch(handleError)
      }),
    [accountIds, addAccountsToGroup, handleSubmit, onAddToGroup, onClose]
  )

  useEffect(() => {
    if (!isOpen) return
    reset()
    getAccountGroupList(undefined)
  }, [isOpen, reset, getAccountGroupList])

  return (
    <>
      <HHBaseDialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
        <HHDialogTitle title={T.ADD_TO_GROUP} onClose={onClose} />
        <DialogContent>
          <FormProvider {...addToGroupForm}>
            <Box mt={2}>
              <HHFormSelectField
                fullWidth
                required
                control={control}
                deprecatedLabel={false}
                error={accountGroupIdHelperText}
                helperText={accountGroupIdHelperText}
                label={T.ACCOUNT_GROUPS}
                name="accountGroupId"
                options={groupOptions}
                placeholder={T.SELECT_GROUP}
                rules={REQUIRED_RULES}
              />
            </Box>
          </FormProvider>
        </DialogContent>
        <HHDialogActions>
          <CancelButton size="medium" onClick={onClose} />
          <Box sx={SPACER_SX} />
          <Button size="small" variant="outlined" onClick={handleCreateNewGroup}>
            {T.CREATE_NEW_GROUP}
          </Button>
          <Button loading={isAddToGroupLoading} size="small" variant="contained" onClick={handleAdd}>
            {T.ADD}
          </Button>
        </HHDialogActions>
      </HHBaseDialog>
      <AddEditAccountGroupDialog
        isOpen={isOpenAddEditAccountGroupDialog}
        onClose={handleCloseCreateNewGroup}
        onSuccess={handleCreateNewGroupSuccess}
      />
    </>
  )
}

export default AddToGroupDialog
